package dev.lingvo.site.pages.app.projects.slug

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.flexGrow
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.rememberPageContext
import dev.lingvo.shared.ProposalDto
import dev.lingvo.shared.ProposalPageDto
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.Session
import dev.lingvo.site.api.Async
import dev.lingvo.site.api.loadReloadable
import dev.lingvo.site.components.LocalAppShellProjectReads
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LAsyncContent
import dev.lingvo.site.ds.LBadge
import dev.lingvo.site.ds.LBadgeVariant
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonSize
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LCard
import dev.lingvo.site.ds.LEmptyState
import dev.lingvo.site.ds.LEmptyStateIcon
import dev.lingvo.site.ds.LLinkButton
import dev.lingvo.site.ds.LBotIcon
import dev.lingvo.site.ds.LCheckCheckIcon
import dev.lingvo.site.ds.LCloseIcon
import dev.lingvo.site.ds.LModal
import dev.lingvo.site.ds.LMuted
import dev.lingvo.site.ds.LDiffText
import dev.lingvo.site.ds.PageHeader
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.ds.enterStaggered
import dev.lingvo.site.ds.monoFont
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tPlural
import dev.lingvo.site.i18n.tf
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.i18n.trf
import dev.lingvo.site.state.ProjectActionClaim
import dev.lingvo.site.state.ProjectAuthority
import dev.lingvo.site.state.ProjectPrivilege
import dev.lingvo.site.state.authoritySubject
import dev.lingvo.site.state.liveProjectRoute
import dev.lingvo.site.state.requestProjectResource
import kotlinx.coroutines.awaitCancellation
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/**
 * What the agents suggested.
 *
 * **A proposal is not a translation.** Nothing here is in an export, in a progress number, or in
 * anybody's way; accepting one writes a translation with *your* name on the acceptance, because the
 * agent proposed and a person decided.
 *
 * Runs can be accepted together, because that is the review people actually do — one-at-a-time for
 * four hundred strings is a review nobody does.
 */
@Page("/app/projects/{slug}/proposals")
@Layout(".components.layouts.AppLayout")
@Composable
fun ProposalsPage() {
    val ctx = rememberPageContext()
    val shellReads = LocalAppShellProjectReads.current
    val slug = ctx.route.params["slug"].orEmpty()
    val verifiedRoute = liveProjectRoute(slug)
    val authority = ProjectAuthority.resolve(verifiedRoute?.project, Session.organizations)
    val authoritySubject = verifiedRoute?.authoritySubject(Session.organizations)
    val projectOverviewHref = verifiedRoute?.takeIf {
        authoritySubject != null && authority.canReadProject
    }?.project?.slug?.let { "/app/projects/${encodeURIComponent(it)}" }
    val decisionClaim = ProjectActionClaim.capture(
        verifiedRoute,
        Session.organizations,
        ProjectPrivilege.PROPOSAL_DECISION,
    )
    var refreshKey by remember(authoritySubject) { mutableStateOf(0) }
    val proposalState = remember(authoritySubject) { ProposalsDecisionState() }

    // **`loadReloadable`, keyed on the subject with the refresh as a *token*.**
    //
    // As a key, `refreshKey` discarded the value before the request was even sent: accepting one
    // proposal blanked the whole review list to a skeleton and then replayed every card's staggered
    // entrance, on an action the reader had just taken and while they were reading the list they
    // were working through. The motion contract forbids exactly that under "review-card
    // advancement" and "skeleton-to-content dimensions".
    //
    // A token reloads underneath instead: the list the reader is looking at stays on screen, and a
    // refresh that fails leaves the last good answer up with a notice rather than an empty page.
    val reloadable = loadReloadable(authoritySubject, reloadToken = refreshKey) {
        val subject = authoritySubject
        if (subject == null) {
            // `liveProjectRoute` answers null while the route is still being verified. This was a
            // `requireNotNull`, so an ordinary wait arrived at the reader as
            // `Async.Failure("Required value was null.")` — a developer sentence rendered as the
            // page's own error. Waiting is a wait: stay in the loading state, and let the changed
            // key restart the load the moment the route resolves.
            awaitCancellation()
        }
        val readAdmission = proposalState.readAdmission()
        val load: suspend () -> ProposalPageDto = {
            requestProjectResource(
                key = subject.action,
                expectedSlug = slug,
                memberships = Session.organizations,
            ) { route ->
                ApiClient.proposals(route.project.slug)
            }
        }
        val page = if (shellReads == null) load() else shellReads.observeProposals(subject, load)
        proposalState.readCompleted(readAdmission)
        page
    }

    val loadedPage = (reloadable.state as? Async.Success)?.value

    // **One decide transport for the whole page, built once and passed down.**
    //
    // The header's run actions and the list's per-card ones are the same write through the same
    // captured claim; two holders would be two `busy` flags, so pressing "Accept all" in the header
    // would leave every card in the list live and pressable against a run that is already being
    // decided. The gate itself is not restated here — [proposalDecisions] owns it, and the surface
    // below uses this instance rather than making a second one.
    val decisions = authoritySubject?.let { subject ->
        proposalDecisions(
            actionStateKey = subject,
            pageClaim = decisionClaim,
            slug = slug,
            state = proposalState,
            knownProposals = loadedPage?.proposals.orEmpty(),
            onChanged = {
                shellReads?.invalidateProposals(subject)
                refreshKey++
            },
        )
    }

    // The run actions belong in the header only when there is exactly one run to act on. With two,
    // a header button could not say which run it decided — and the rows in the list can, because
    // each sits beside its own run's name and count.
    val runs = loadedPage?.proposals?.filterNot { it.id in proposalState.acknowledged }?.let(::proposalRuns).orEmpty()
    val soleRun = runs.singleOrNull()?.takeIf { authority.canDecideProposals && decisions != null }

    Column(Modifier.fillMaxWidth().gap(Space.XL)) {
        PageHeader(
            t("app.projects.slug.proposals.proposals", "Proposals"),
            // The count the page is about, in the one place a reader looks before scrolling. It is
            // every pending proposal in the project, not the page of them below.
            subtitle = loadedPage?.let { page ->
                tPlural(
                    "app.projects.slug.proposals.count.pending_total",
                    page.total,
                    "{count} suggestion from agents is waiting. Nothing here is live until you accept it.",
                    "{count} suggestions from agents are waiting. Nothing here is live until you accept it.",
                )
            } ?: t("app.projects.slug.proposals.suggestions_from_agents_nothing_here_is", "Suggestions from agents. Nothing here is live until you accept it."),
            // The run the header's pair decides, named. Moving the two buttons up took the row
            // that said "Nightly agent · 12 proposals on this page" with them, and a control that
            // settles a whole run must not be the only thing on screen that knows which run.
            meta = if (soleRun != null) {
                {
                    Span(Modifier.toAttrs { attr("data-proposals-run", "true") }) {
                        // The join is part of the message, not a `+` outside it: a locale that
                        // punctuates a run name differently — or that wants the count first — can
                        // only say so if the separator is inside the string it is handed.
                        Text(
                            tPlural(
                                "app.projects.slug.proposals.count.run_proposals_on_this_page",
                                soleRun.shown,
                                "{run} · {count} proposal on this page",
                                "{run} · {count} proposals on this page",
                                "run" to soleRun.label,
                            ),
                        )
                    }
                }
            } else {
                null
            },
            actions = if (soleRun != null && decisions != null) {
                {
                    ProposalRunDecision(
                        run = soleRun,
                        coversUnshown = (loadedPage?.total ?: 0) > (loadedPage?.proposals?.size ?: 0),
                        busy = decisions.busy || decisions.blocked,
                        // The header's own scale, not the list's SM: with a single run waiting,
                        // this pair is the screen's task.
                        size = LButtonSize.MD,
                        onDecide = decisions.decide,
                    )
                }
            } else {
                null
            },
        )

        ProposalsAcceptedDrafts(slug, proposalState, onReload = { refreshKey++ })

        reloadable.refreshError?.let { failure ->
            LAlert(
                title = t("app.projects.slug.proposals.showing_the_last_list", "Showing the last list that loaded"),
                description = failure.readable(),
                modifier = Modifier.fillMaxWidth(),
                variant = LAlertVariant.WARNING,
                // Only ever set over a success already on screen, and only from a reader-initiated
                // reload, so it is information rather than an interruption.
                live = true,
                action = {
                    LButton(
                        label = t("app.projects.slug.proposals.retry", "Retry"),
                        onClick = { refreshKey++ },
                        variant = LButtonVariant.GHOST,
                        size = LButtonSize.SM,
                    )
                },
            )
        }

        LAsyncContent(
            reloadable.state,
            rows = 5,
            loadingLabel = t("app.projects.slug.proposals.loading_proposals", "Loading proposals"),
            onRetry = { refreshKey++ },
        ) { page ->
            // `ProposalDecisionSurface` emits its failure alert above the list as a sibling, and
            // this page's column is what spaced the two apart. The content slot is one plain
            // `<div>`, so the same gap has to be reinstated inside it.
            Column(Modifier.fillMaxWidth().gap(Space.XL)) {
                authoritySubject?.let { subject ->
                    ProposalDecisionSurface(
                        proposals = page.proposals,
                        // Everything pending, which is not the same as what fits on this page.
                        total = page.total,
                        authority = authority,
                        actionStateKey = subject,
                        pageClaim = decisionClaim,
                        slug = slug,
                        projectOverviewHref = projectOverviewHref,
                        decisions = decisions,
                        // Drawn once. When the header carries the sole run's pair, a second pair
                        // above the list is the same two controls twice.
                        showRunActions = soleRun == null,
                        showFeedback = false,
                        onChanged = { refreshKey++ },
                    )
                }
            }
        }
    }
}

/**
 * A whole-run decision waiting for its confirmation.
 *
 * [shown] is what this screen has loaded; [coversUnshown] says the request settles more than that.
 */
private data class RunDecision(
    val runId: String,
    val label: String,
    val shown: Int,
    val stale: Int,
    val coversUnshown: Boolean,
    val accept: Boolean,
)

/**
 * One agent run, as much of it as this page of proposals shows.
 *
 * Derived rather than stored, and derived in one function because the header and the list both
 * need the same answer to "which runs are pending, and what is each one called".
 */
internal data class ProposalRun(
    val runId: String,
    val label: String,
    val shown: Int,
    val stale: Int,
)

/** `trf`, not `tf`: this is a plain function, called before the header's composition begins. */
internal fun proposalRuns(proposals: List<ProposalDto>): List<ProposalRun> =
    proposals.mapNotNull { it.runId }.distinct().map { runId ->
        val inRun = proposals.filter { it.runId == runId }
        ProposalRun(
            runId = runId,
            label = inRun.firstOrNull()?.agentName
                ?: trf(
                    "app.projects.slug.proposals.run_short_id",
                    "Run {id}",
                    "id" to runId.take(8),
                ),
            shown = inRun.size,
            stale = inRun.count { it.stale },
        )
    }

/**
 * Accept all / Reject all for one run, with the gate that asks first.
 *
 * The pair and its confirmation travel together because they are one control: a button that opens
 * a modal it does not own is a button whose press can go nowhere. Only one caller draws it at a
 * time — the header when a single run is pending, the list's run rows when several are — so there
 * is never a second modal in the tree.
 */
@Composable
private fun ProposalRunDecision(
    run: ProposalRun,
    coversUnshown: Boolean,
    busy: Boolean,
    size: LButtonSize,
    onDecide: (ids: List<String>, runId: String?, accept: Boolean) -> Unit,
) {
    var pending by remember(run.runId) { mutableStateOf<RunDecision?>(null) }

    LButton(
        t("app.projects.slug.proposals.accept_all", "Accept all"),
        {
            pending = RunDecision(run.runId, run.label, run.shown, run.stale, coversUnshown, accept = true)
        },
        enabled = !busy,
        size = size,
        variant = LButtonVariant.SECONDARY,
        leadingIcon = { LCheckCheckIcon() },
    )
    LButton(
        t("app.projects.slug.proposals.reject_all", "Reject all"),
        {
            pending = RunDecision(run.runId, run.label, run.shown, run.stale, coversUnshown, accept = false)
        },
        enabled = !busy,
        size = size,
        variant = LButtonVariant.GHOST,
        leadingIcon = { LCloseIcon() },
    )
    RunDecisionModal(pending, busy, onDismiss = { pending = null }) { decision ->
        pending = null
        onDecide(emptyList(), decision.runId, decision.accept)
    }
}

@Composable
internal fun ProposalList(
    proposals: List<ProposalDto>,
    canDecide: Boolean,
    busy: Boolean,
    /** Everything pending, so the run actions can say what they reach beyond this page. */
    total: Int = proposals.size,
    /**
     * False when the page has moved the sole run's pair into `PageHeader.actions` (spec §8).
     *
     * The per-card Accept and Reject are unaffected — what moves is the whole-run pair, and it
     * moves rather than being copied.
     */
    showRunActions: Boolean = true,
    acknowledged: Set<String> = emptySet(),
    projectOverviewHref: String? = null,
    onDecide: (ids: List<String>, runId: String?, accept: Boolean) -> Unit,
) {
    if (proposals.isEmpty()) {
        // Pending proposals and already accepted drafts have different destinations.
        LEmptyState(
            t("app.projects.slug.proposals.nothing_waiting", "Nothing waiting"),
            t("app.projects.slug.proposals.when_an_agent_suggests_a_translation", "When an agent suggests a translation it appears here for you to accept. Accepted suggestions become drafts, not approved translations."),
            Modifier.fillMaxWidth(),
            LEmptyStateIcon.PROPOSAL,
            secondaryAction = if (projectOverviewHref != null) {
                {
                    LLinkButton(
                        t("app.projects.slug.proposals.open_project_translations", "Open project translations"),
                        projectOverviewHref,
                        variant = LButtonVariant.SECONDARY,
                    )
                }
            } else null,
        ) {
            // The default PRIMARY, matching the structurally identical empty state on
            // `/data/jobs`. Drawn as SECONDARY this page rendered with no primary action anywhere
            // on it — nothing accent-filled, and two empty states in one lane giving the same
            // gesture two different weights.
            LLinkButton(
                t("app.projects.slug.proposals.connect_an_agent", "Connect an agent"),
                "/app/settings/agents",
                leadingIcon = { LBotIcon() },
            )
        }
        return
    }

    if (!canDecide) {
        LAlert(
            title = t("app.projects.slug.proposals.you_can_review_these_suggestions_but", "You can review these suggestions but not decide them"),
            description = t("app.projects.slug.proposals.a_translator_admin_or_owner_for", "A translator, admin, or owner for this project can accept or reject a proposal."),
            variant = LAlertVariant.INFO,
        )
    }

    // The whole-run action first, because it is the one that gets used.
    //
    // **It decides more than it draws.** `DecideProposalsRequest(runId = …)` settles every pending
    // proposal in the run, while this page renders one server page of them — so the row names which
    // run it belongs to, counts what is on screen, and asks before writing. Two runs used to render
    // as two identical "This run:" rows with nothing telling them apart.
    if (canDecide && showRunActions) {
        val coversUnshown = total > proposals.size
        proposalRuns(proposals.filterNot { it.id in acknowledged }).forEach { run ->
            Row(Modifier.gap(Space.SM), verticalAlignment = Alignment.CenterVertically) {
                Span(Modifier.fontSize(TypeScale.Small).toAttrs()) {
                    Text(
                        // Same message as the header's run line, and the separator is inside it
                        // for the same reason: a locale that punctuates a run name differently, or
                        // that wants the count first, can only say so if the join is translatable.
                        tPlural(
                            "app.projects.slug.proposals.count.run_proposals_on_this_page",
                            run.shown,
                            "{run} · {count} proposal on this page",
                            "{run} · {count} proposals on this page",
                            "run" to run.label,
                        ),
                    )
                }
                ProposalRunDecision(
                    run = run,
                    coversUnshown = coversUnshown,
                    busy = busy,
                    size = LButtonSize.SM,
                    onDecide = onDecide,
                )
            }
        }
    }

    proposals.forEachIndexed { index, proposal ->
        ProposalCard(
            index = index,
            proposal = proposal,
            canDecide = canDecide,
            busy = busy || proposal.id in acknowledged,
            onAccept = { onDecide(listOf(proposal.id), null, true) },
            onReject = { onDecide(listOf(proposal.id), null, false) },
        )
    }
}

/**
 * The gate in front of a whole-run decision.
 *
 * Accepting a run writes translations for every pending proposal it produced — including the ones
 * this page never drew, and including the ones badged "the English changed since", whose per-card
 * warning exists to stop exactly that. Neither fact was anywhere near the button.
 */
@Composable
private fun RunDecisionModal(
    decision: RunDecision?,
    busy: Boolean,
    onDismiss: () -> Unit,
    onConfirm: (RunDecision) -> Unit,
) {
    val accepting = decision?.accept == true
    LModal(
        open = decision != null,
        title = if (accepting) {
            t("app.projects.slug.proposals.accept_this_whole_run", "Accept this whole run?")
        } else {
            t("app.projects.slug.proposals.reject_this_whole_run", "Reject this whole run?")
        },
        onClose = { if (!busy) onDismiss() },
        actions = {
            LButton(
                t("app.projects.slug.proposals.cancel", "Cancel"),
                onDismiss,
                variant = LButtonVariant.GHOST,
                enabled = !busy,
            )
            LButton(
                if (accepting) {
                    t("app.projects.slug.proposals.accept_the_run", "Accept the run")
                } else {
                    t("app.projects.slug.proposals.reject_the_run", "Reject the run")
                },
                { decision?.let(onConfirm) },
                variant = if (accepting) LButtonVariant.SECONDARY else LButtonVariant.DANGER,
                enabled = !busy,
                loading = busy,
            )
        },
    ) {
        Column(Modifier.fillMaxWidth().gap(Space.SM)) {
            // One sentence per meaning, each with its own key: a translator handed three fragments
            // to join cannot make the count agree with the verb in their own grammar.
            Text(
                if (accepting) {
                    tf(
                        "app.projects.slug.proposals.accepting_writes_a_translation_for_every",
                        "Accepting writes a translation for every pending proposal in {run}.",
                        "run" to decision?.label.orEmpty(),
                    )
                } else {
                    tf(
                        "app.projects.slug.proposals.rejecting_discards_every_pending_proposal_in",
                        "Rejecting discards every pending proposal in {run}.",
                        "run" to decision?.label.orEmpty(),
                    )
                },
            )
            Text(
                if (decision?.coversUnshown == true) {
                    tPlural(
                        "app.projects.slug.proposals.count.shown_here_and_more",
                        decision.shown,
                        "{count} is on this page, and the run's remaining proposals are decided with it.",
                        "{count} are on this page, and the run's remaining proposals are decided with them.",
                    )
                } else {
                    tPlural(
                        "app.projects.slug.proposals.count.shown_here",
                        decision?.shown ?: 0,
                        "{count} is on this page.",
                        "{count} are on this page.",
                    )
                },
            )
            if ((decision?.stale ?: 0) > 0) {
                LAlert(
                    title = tPlural(
                        "app.projects.slug.proposals.count.stale_here",
                        decision?.stale ?: 0,
                        "{count} of these answers English that has since changed",
                        "{count} of these answer English that has since changed",
                    ),
                    description = if (accepting) {
                        t("app.projects.slug.proposals.accepting_them_imports_translations_of_text", "Accepting them imports translations of text that no longer exists.")
                    } else {
                        t("app.projects.slug.proposals.rejecting_them_is_usually_right_the", "Rejecting them is usually right: the text they answer is gone.")
                    },
                    modifier = Modifier.fillMaxWidth(),
                    variant = if (accepting) LAlertVariant.DANGER else LAlertVariant.WARNING,
                )
            }
        }
    }
}

/**
 * How sure the agent said it was, in words rather than a bare enum name.
 *
 * Three literals rather than one interpolated string, because the extractor never sees a sentence
 * assembled at runtime and a label nobody can translate is a label that stays English for everyone.
 */
@Composable
private fun confidenceLabel(confidence: String): String = when (confidence) {
    "high" -> t("app.projects.slug.proposals.confident", "confident")
    "low" -> t("app.projects.slug.proposals.unsure", "unsure")
    else -> t("app.projects.slug.proposals.fairly_sure", "fairly sure")
}

@Composable
private fun ProposalCard(
    index: Int,
    proposal: ProposalDto,
    canDecide: Boolean,
    busy: Boolean,
    onAccept: () -> Unit,
    onReject: () -> Unit,
) {
    LCard(Modifier.fillMaxWidth().enterStaggered(index)) {
        Column(Modifier.fillMaxWidth().gap(Space.SM)) {
            Row(Modifier.fillMaxWidth().gap(Space.SM), verticalAlignment = Alignment.CenterVertically) {
                Span(Modifier.flexGrow(1).monoFont().fontSize(TypeScale.Small).toAttrs()) {
                    Text(tf("app.projects.slug.proposals.key_and_locale", "{key} · {locale}", "key" to proposal.keyName, "locale" to proposal.locale))
                }
                proposal.agentName?.let { LBadge(it) }
                // **The cheapest triage a reviewer has.** Deciding four thousand German strings
                // needs an order to read them in, and without this every proposal looks equally
                // trustworthy. `low` is deliberately quiet rather than alarming: it is an agent
                // being honest, not an error.
                proposal.confidence?.let { confidence ->
                    LBadge(
                        confidenceLabel(confidence),
                        variant = when (confidence) {
                            "high" -> LBadgeVariant.SUCCESS
                            "low" -> LBadgeVariant.NEUTRAL
                            else -> LBadgeVariant.INFO
                        },
                    )
                }
                if (proposal.stale) {
                    // Accepting it would import a translation of text that no longer exists.
                    LBadge(t("app.projects.slug.proposals.the_english_changed_since", "Source changed since this proposal"), variant = LBadgeVariant.WARNING)
                }
            }

            // **A plural proposal leaves `value` null** and carries its text in `pluralForms`
            // (`ProposalRepo.pending` maps `plural_forms` straight through). Printing `value` alone
            // drew a blank "Suggested" above two live Accept/Reject buttons — the permalink page
            // wrote down the same reason for the same fix.
            val suggested = proposal.suggestedText()
            // Why, in the agent's own words, beside what it suggested. The agent is the only party
            // who can supply it: after the fact nobody can reconstruct what it was unsure about.
            proposal.rationale?.takeIf(String::isNotBlank)?.let { rationale ->
                LMuted(rationale)
            }
            val current = proposal.currentValue?.takeIf(String::isNotBlank)
            ProposalsSource(proposal)
            if (current != null && suggested.isNotBlank()) {
                // **Now → Suggested as one diff, not two paragraphs.** Deciding a proposal is
                // deciding what changed, and two blocks of near-identical text is the reader doing
                // the comparison the screen could have done.
                Column(Modifier.fillMaxWidth().gap(Space.XXS)) {
                    LMuted(t("app.projects.slug.proposals.now_suggested", "Now → Suggested"))
                    Span(Modifier.fontSize(TypeScale.BodySmall).toAttrs()) {
                        LDiffText(current, suggested)
                    }
                }
            } else {
                current?.let {
                    // What would be replaced, shown rather than left to be guessed at.
                    Labelled(t("app.projects.slug.proposals.now", "Now"), it)
                }
                Labelled(
                    t("app.projects.slug.proposals.suggested", "Suggested"),
                    suggested.ifBlank { "—" },
                )
            }
            // Plural categories one per row: "one form changed" is precisely what a single joined
            // line hides, and a plural proposal is the shape most likely to be wrong.
            proposal.pluralForms?.takeIf { it.isNotEmpty() }?.let { forms ->
                Column(Modifier.fillMaxWidth().gap(Space.XXS)) {
                    forms.entries.sortedBy { it.key.ordinal }.forEach { (category, value) ->
                        LMuted(
                            tf(
                                "app.projects.slug.proposals.plural_category_value",
                                "{category}: {value}",
                                "category" to category.name.lowercase(),
                                "value" to value,
                            ),
                        )
                    }
                }
            }

            if (canDecide) {
                Row(Modifier.gap(Space.SM)) {
                    // Accepting text that is not there writes an empty translation, so the reject
                    // half stays live and this one does not.
                    // Every card renders this pair, so a screen reader's button list was "Accept"
                    // and "Reject" repeated with nothing naming the string each decides — on a
                    // screen whose whole job is deciding one string at a time.
                    LButton(
                        t("app.projects.slug.proposals.accept", "Accept"),
                        onAccept,
                        enabled = !busy && suggested.isNotBlank(),
                        size = LButtonSize.SM,
                        variant = LButtonVariant.SECONDARY,
                        accessibleLabel = trf(
                            "app.projects.slug.proposals.accept_key_in_locale",
                            "Accept {key} in {locale}",
                            "key" to proposal.keyName,
                            "locale" to proposal.locale,
                        ),
                    )
                    LButton(
                        t("app.projects.slug.proposals.reject", "Reject"),
                        onReject,
                        enabled = !busy,
                        size = LButtonSize.SM,
                        variant = LButtonVariant.GHOST,
                        accessibleLabel = trf(
                            "app.projects.slug.proposals.reject_key_in_locale",
                            "Reject {key} in {locale}",
                            "key" to proposal.keyName,
                            "locale" to proposal.locale,
                        ),
                    )
                }
                LMuted(t("app.projects.slug.proposals.accepting_writes_it_as_a_translation", "Accepting writes it as a translation for review, never as approved."))
            }
        }
    }
}

/**
 * What the agent actually suggested, whichever shape it came in.
 *
 * A single-form proposal is [ProposalDto.value]; a plural one leaves that null and fills
 * [ProposalDto.pluralForms]. The categories are listed in CLDR order so two proposals for the same
 * language read the same way down the page.
 */
private fun ProposalDto.suggestedText(): String =
    value?.takeIf(String::isNotBlank)
        ?: pluralForms
            .orEmpty()
            .entries
            .sortedBy { it.key.ordinal }
            // The same catalogued line the expanded rows below render, not a second hand-built
            // spelling of it: `"$category: $value"` renders identically in English and in no other
            // language, which is how two versions of one sentence sat on adjacent surfaces. `trf`
            // rather than `tf` because this is a plain function, read outside composition.
            .joinToString(" · ") { (category, value) ->
                trf(
                    "app.projects.slug.proposals.plural_category_value",
                    "{category}: {value}",
                    "category" to category.name.lowercase(),
                    "value" to value,
                )
            }

@Composable
private fun Labelled(label: String, value: String) {
    Column(Modifier.fillMaxWidth().gap(Space.XXS)) {
        LMuted(label)
        Span(Modifier.fontSize(TypeScale.BodySmall).toAttrs()) { Text(value) }
    }
}

private external fun encodeURIComponent(value: String): String

